import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadOwnKycDocument, useFileKycDocument, useKycPack, useRemoveKycDocument } from '../../api/hooks';
import { useSession } from '../../auth/session';
import { Alert, Button, Chip, Panel, Spinner, Table, type Tone } from '../../ui';
import { megabytes, reasonDisplay, standing, type KycStanding } from './kyc-view';

/**
 * R-18's KYC panel — CR-57 / D105.
 *
 * D105 supersedes D94: KYC no longer gates the ACCOUNT, it gates the ORDER. So a new retailer lands
 * here with an account, can browse and fill a cart, and files their own documents; Admin approves
 * the pack on A-04; only then can a PO be submitted. This panel is where they do the filing.
 *
 * **The rejection reason is the most important thing on it.** A retailer who is not told which file
 * was wrong uploads the same one again, and then rings their account manager — the very thing this
 * feature exists to prevent. So it leads the panel, in Miraki's own words and unabridged, and it
 * stays visible after they have re-filed (the account is back in review, the reason is still the
 * last thing Miraki said) until Admin's next decision replaces it.
 *
 * The state comes from `me` (so every login on the account can see it) and the documents from
 * `GET /shop/kyc-documents`, which only the Owner (`shop.profile`) may call — filing an account's
 * identity documents is the job of whoever administers the account, and nobody else is sent a
 * control that would be refused.
 *
 * The window is the server's: `mayFile` says whether filing will work, and the server re-checks it
 * from the database on every request.
 */
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,.doc,.docx,application/msword';

const TONE: Record<KycStanding, Tone> = { NEEDED: 'stop', REVIEW: 'run', APPROVED: 'done', REJECTED: 'stop' };

export function KycPanel() {
  const { t } = useTranslation();
  const { me } = useSession();
  const isOwner = me?.kind === 'retailer' && me.permissions.includes('shop.profile');
  const pack = useKycPack(isOwner);
  const file = useFileKycDocument();
  const remove = useRemoveKycDocument();
  const picker = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  // The document being replaced: the picker opens once for it, and the old file goes after the new one lands.
  const [replacing, setReplacing] = useState<string | null>(null);

  if (me?.kind !== 'retailer') return null;

  // The state every login can see, from `me`; the documents only the Owner is sent.
  const status = pack.data?.kycStatus ?? me.kycStatus;
  const reason = pack.data?.kycRejectionReason ?? me.kycRejectionReason;
  const documents = pack.data?.documents ?? [];
  const stand = standing(status, documents.length);
  const shown = reasonDisplay(status, reason);
  const mayFile = isOwner && (pack.data?.mayFile ?? true);
  const full = (pack.data?.remaining ?? 1) <= 0;
  const busy = file.isPending || remove.isPending || removing !== null;

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    e.target.value = ''; // so the same file can be chosen again after a refusal
    const toReplace = replacing;
    setReplacing(null);
    if (!chosen) return;
    setProblem(null);
    try {
      // A full pack has no room for the new file first, so the old one goes first; otherwise the new
      // one is filed first, so a failed upload never leaves the retailer with nothing.
      if (toReplace && full) await remove.mutateAsync(toReplace);
      await file.mutateAsync(chosen);
      if (toReplace && !full) await remove.mutateAsync(toReplace);
    } catch (err) {
      // The API says what is wrong with the file — the count, the size, or that it is not the
      // format its name claims — and says it plainly, so it is shown as it arrived.
      setProblem((err as Error).message);
    }
  };

  const onRemove = async (id: string) => {
    setProblem(null);
    setRemoving(id);
    try {
      await remove.mutateAsync(id);
    } catch {
      setProblem(t('shop.kyc.removeFailed'));
    } finally {
      setRemoving(null);
    }
  };


  return (
    <Panel
      title={t('shop.kyc.title')}
      pad
      actions={<Chip tone={TONE[stand]}>{t(`shop.kyc.status.${stand}`)}</Chip>}
    >
      {/* The reason leads — see the note above. Shown while REJECTED, and while re-filed and awaiting review. */}
      {shown !== 'none' ? (
        <Alert tone={shown === 'rejected' ? 'stop' : 'them'}>
          <b>{shown === 'rejected' ? t('shop.kyc.rejectedTitle') : t('shop.kyc.earlierNote')}</b>{' '}
          {shown === 'rejected' ? t('shop.kyc.rejectedLead') : null}
          <div style={{ marginTop: 'var(--mk-space-2)', whiteSpace: 'pre-wrap' }}>{reason ?? t('shop.kyc.noReason')}</div>
        </Alert>
      ) : null}

      {stand === 'APPROVED' ? <p>{t('shop.kyc.approvedBody')}</p> : null}
      {stand === 'REVIEW' ? <p>{t('shop.kyc.reviewBody')}</p> : null}
      {stand === 'NEEDED' || stand === 'REJECTED' ? <p>{t('shop.kyc.intro')}</p> : null}

      {!isOwner ? (
        <p className="mk-dim">{t('shop.kyc.ownerOnly')}</p>
      ) : pack.isLoading ? (
        <Spinner />
      ) : pack.error ? (
        <Alert tone="stop">{t('shop.kyc.loadFailed')}</Alert>
      ) : (
        <>
          {documents.length === 0 ? (
            <p className="mk-dim">{t('shop.kyc.none')}</p>
          ) : (
            <Table
              rows={documents}
              rowKey={(d) => d.id}
              columns={[
                {
                  header: t('shop.kyc.colFile'),
                  render: (d) => (
                    <>
                      {d.fileName}
                      <div className="mk-dim">{d.source === 'APPLICATION' ? t('shop.kyc.withApplication') : t('shop.kyc.addedLater')}</div>
                    </>
                  ),
                },
                { header: t('shop.kyc.colSize'), numeric: true, render: (d) => megabytes(d.sizeBytes) },
                { header: t('shop.kyc.colSent'), render: (d) => d.createdAt.slice(0, 10) },
                {
                  header: '',
                  numeric: true,
                  render: (d) => (
                    <span style={{ display: 'inline-flex', gap: 'var(--mk-space-2)', justifyContent: 'flex-end' }}>
                      {/* Download is for anyone who may see the pack; replace and delete need the window. */}
                      <Button size="sm" tone="ghost" aria-label={`${t('shop.kyc.download')}: ${d.fileName}`}
                        onClick={() => { void downloadOwnKycDocument(d).catch((e: unknown) => { setProblem((e as Error).message); }); }}>
                        ⭳ {t('shop.kyc.download')}
                      </Button>
                      {mayFile ? (
                        <>
                          <Button size="sm" tone="ghost" disabled={busy}
                            onClick={() => { setReplacing(d.id); picker.current?.click(); }}>
                            {replacing === d.id && file.isPending ? t('shop.kyc.replacing') : t('shop.kyc.replace')}
                          </Button>
                          <Button size="sm" tone="ghost" disabled={busy} onClick={() => { void onRemove(d.id); }}>
                            {removing === d.id ? t('shop.kyc.removing') : t('shop.kyc.remove')}
                          </Button>
                        </>
                      ) : null}
                    </span>
                  ),
                },
              ]}
            />
          )}

          {problem ? <Alert tone="stop">{problem}</Alert> : null}

          {mayFile ? (
            <div style={{ marginTop: 'var(--mk-space-3)' }}>
              <input ref={picker} type="file" accept={ACCEPT} hidden onChange={(e) => { void onPick(e); }} />
              <Button disabled={busy || full} onClick={() => { setReplacing(null); picker.current?.click(); }}>
                {file.isPending ? t('shop.kyc.sending') : t('shop.kyc.choose')}
              </Button>
              <p className="mk-hint">{t('shop.kyc.changeNote')}</p>
              <p className="mk-hint">
                {full
                  ? t('shop.kyc.limitReached')
                  : t('shop.kyc.limits', {
                    max: pack.data?.documentsAccepted ?? 5, mb: 8, count: documents.length,
                  })}
              </p>
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
}
